import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIFormControl from 'UIComponents/form/UIFormControl';
import H2 from 'UIComponents/elements/headings/H2';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { PIPELINE_STAGE_IN_PIPELINE } from '../../../resolvers/CustomResolverReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { createCachedCustomResolver } from '../../../resolvers/createCachedCustomResolver';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { referenceToOptionWithReference } from '../../../utils/referenceLiteSearchSelectUtils';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { useGetLoadStageReferences } from '../hooks/useGetLoadStageReferences';
import { useRecordInitialPipelineAndStage } from '../hooks/useRecordInitialPipelineAndStage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { useIsBETPortal } from '../../../v2/hooks/useIsBETPortal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PipelineModal = props => {
  const {
    onConfirm,
    onCancel,
    defaultPipelineId,
    defaultPipelineStageId,
    inputMode,
    stageChangeValidatorLoading,
    showAutoSaveText = false
  } = props;
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const httpClient = useHttpClient();
  const isBETPortal = useIsBETPortal();
  const [pipelineId, setPipelineId] = useState(defaultPipelineId);
  const [pipelineStageId, setPipelineStageId] = useState(defaultPipelineStageId);
  const [stageReference, setStageReference] = useState(null);
  const {
    recordPipelineId,
    recordStageId,
    propertyValuesLoading
  } = useRecordInitialPipelineAndStage({
    httpClient,
    objectTypeId,
    inputMode,
    objectId
  });
  const loadStageReferences = useGetLoadStageReferences({
    httpClient,
    objectTypeId,
    pipelineId,
    inputMode,
    objectId
  });

  // NOTE: we cannot use `usePropertyComponentResolver` here because we need
  // `PIPELINE_WITH_PERMISSIONS` to ensure that PLP-hidden pipelines are disabled.
  // Making `PIPELINE_WITH_PERMISSIONS` the default for pipeline properties in
  // `getReferenceTypeFromProperty` (instead of `PIPELINE`) would break
  // filters/workflows by preventing users from selecting these pipelines
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient
  });

  // parameterize the resolver with `pipelineId` so it only shows stages in the selected pipeline
  const pipelineStageResolver = createCachedCustomResolver(PIPELINE_STAGE_IN_PIPELINE, objectTypeId, {
    pipelineId: pipelineId === null ? undefined : pipelineId,
    httpClient
  });

  // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).
  const handlePipelineChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newPipelineId = `${e.target.value.value}`;
    const defaultStageId = reference && reference.additionalProperties && reference.additionalProperties.defaultPipelineStageId ? `${reference.additionalProperties.defaultPipelineStageId}` : null;
    const newStageId = !propertyValuesLoading && newPipelineId === recordPipelineId && recordStageId ? recordStageId : defaultStageId;
    setPipelineId(newPipelineId);
    setPipelineStageId(isBETPortal ? null : newStageId);
  }, [recordPipelineId, recordStageId, propertyValuesLoading, isBETPortal]);
  const handlePipelineStageChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newStageId = `${e.target.value.value}`;
    const newPipelineId = reference && reference.additionalProperties && reference.additionalProperties.pipelineId ? `${reference.additionalProperties.pipelineId}` : null;
    setPipelineId(newPipelineId);
    setPipelineStageId(newStageId);
    setStageReference(reference);
  }, []);
  const handleConfirm = useCallback(() => {
    onConfirm({
      pipelineId,
      pipelineStageId,
      stageReference: stageReference !== null && stageReference !== void 0 ? stageReference : undefined
    });
  }, [onConfirm, pipelineId, pipelineStageId, stageReference]);
  const disabled = !(pipelineId && pipelineStageId);
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.apply"
  });
  return /*#__PURE__*/_jsxs(UIModal, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.header"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCancel
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [propertyValuesLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered"
      }), !propertyValuesLoading && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.pipeline"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReference,
            onSelectedOptionChange: handlePipelineChange,
            resolver: pipelineResolver,
            value: pipelineId
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.stage"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReference,
            onSelectedOptionChange: handlePipelineStageChange,
            resolver: pipelineStageResolver,
            value: pipelineStageId,
            loadReferences: loadStageReferences
            // The reason we don't need to add cache={false} here is because the record can't get saved while the modal is visible
            // For a smoother experience, we can utilize UISelect's option cache since we don't need to refetch validation when the input opens
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.confirmDisabledTooltip"
        }),
        disabled: !disabled,
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          onClick: handleConfirm,
          disabled: disabled || stageChangeValidatorLoading,
          children: confirmLabel
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.cancel"
        })
      })]
    })]
  });
};
PipelineModal.displayName = 'PipelineModal';
export default PipelineModal;